/**
 * FERRAMENTA CENTRAL DE BACKUP E RESTAURAÇÃO — DOMAIN MODELS
 * PROJETO: INSPECT IA
 * ESCOPO: ADMINISTRAÇÃO > BACKUP E RESTAURAÇÃO
 */

export type BackupType = "MANUAL" | "AUTOMATIC" | "SAFETY_SNAPSHOT";
export type BackupScope = "FULL" | "DATABASE" | "FILES" | "SETTINGS";
export type BackupStatus = "COMPLETED" | "FAILED" | "IN_PROGRESS" | "CORRUPTED";

export interface BackupManifest {
  readonly id: string;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly createdById: string;
  readonly clientId: string;
  readonly systemVersion: string;
  readonly schemaVersion: number;
  readonly type: BackupType;
  readonly scope: BackupScope;
  readonly description: string;
  readonly sizeBytes: number;
  readonly sha256Checksum: string;
  readonly entityCounts: Readonly<Record<string, number>>;
  readonly fileIntegrityMap: Readonly<Record<string, string>>;
  readonly compatibilityTags: readonly string[];
}

export interface BackupPackage {
  readonly manifest: BackupManifest;
  readonly databasePayload?: Readonly<Record<string, readonly unknown[]>>;
  readonly filesPayload?: Readonly<Record<string, { readonly mimeType: string; readonly dataBase64: string; readonly sizeBytes: number }>>;
  readonly settingsPayload?: Readonly<Record<string, unknown>>;
}

export interface BackupValidationResult {
  readonly valid: boolean;
  readonly manifest?: BackupManifest;
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
  readonly compatible: boolean;
  readonly entitySummary: Readonly<Record<string, number>>;
  readonly integrityChecked: boolean;
}

export interface CreateBackupParams {
  readonly scope?: BackupScope;
  readonly type?: BackupType;
  readonly description?: string;
  readonly customClientId?: string;
}

export interface RestoreExecutionParams {
  readonly backupId: string;
  readonly packageData?: BackupPackage;
  readonly confirmationToken: string;
  readonly createSafetySnapshot?: boolean;
  readonly selectiveEntities?: readonly string[];
}

export interface RestoreExecutionResult {
  readonly success: boolean;
  readonly backupId: string;
  readonly safetySnapshotId?: string;
  readonly restoredEntities: Readonly<Record<string, number>>;
  readonly executedAt: string;
  readonly executedBy: string;
  readonly durationMs: number;
  readonly errors: readonly string[];
}

export interface RestoreHistoryEntry {
  readonly id: string;
  readonly restoredAt: string;
  readonly restoredBy: string;
  readonly sourceBackupId: string;
  readonly sourceBackupType: BackupType;
  readonly safetySnapshotId: string;
  readonly status: "SUCCESS" | "FAILED" | "ROLLED_BACK";
  readonly details: string;
  readonly entityCounts: Readonly<Record<string, number>>;
}

export interface RetentionPolicy {
  readonly maxBackupsTotal: number;
  readonly maxSafetySnapshots: number;
  readonly autoCleanupEnabled: boolean;
  readonly retentionDays: number;
}

export interface RetentionResult {
  readonly deletedBackupIds: readonly string[];
  readonly retainedCount: number;
  readonly totalFreedBytes: number;
}

export interface BackupStorageMetrics {
  readonly totalBackups: number;
  readonly manualCount: number;
  readonly automaticCount: number;
  readonly safetySnapshotCount: number;
  readonly totalSizeBytes: number;
  readonly lastBackupDate: string | null;
  readonly healthStatus: "HEALTHY" | "WARNING" | "ATTENTION_REQUIRED";
}

export const DEFAULT_RETENTION_POLICY: RetentionPolicy = {
  maxBackupsTotal: 30,
  maxSafetySnapshots: 10,
  autoCleanupEnabled: true,
  retentionDays: 90
};

export const CURRENT_SYSTEM_VERSION = "1.0.0";
export const CURRENT_SCHEMA_VERSION = 11;

/**
 * Pure TypeScript SHA-256 hash calculation compatible with both Node and Browser.
 */
export function calculateSha256(content: string): string {
  // UTF-8 encode
  const utf8: number[] = [];
  for (let i = 0; i < content.length; i++) {
    let charcode = content.charCodeAt(i);
    if (charcode < 0x80) utf8.push(charcode);
    else if (charcode < 0x800) {
      utf8.push(0xc0 | (charcode >> 6), 0x80 | (charcode & 0x3f));
    } else if (charcode < 0xd800 || charcode >= 0xe000) {
      utf8.push(0xe0 | (charcode >> 12), 0x80 | ((charcode >> 6) & 0x3f), 0x80 | (charcode & 0x3f));
    } else {
      i++;
      charcode = 0x10000 + (((charcode & 0x3ff) << 10) | (content.charCodeAt(i) & 0x3ff));
      utf8.push(
        0xf0 | (charcode >> 18),
        0x80 | ((charcode >> 12) & 0x3f),
        0x80 | ((charcode >> 6) & 0x3f),
        0x80 | (charcode & 0x3f)
      );
    }
  }

  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
  ];

  let H0 = 0x6a09e667;
  let H1 = 0xbb67ae85;
  let H2 = 0x3c6ef372;
  let H3 = 0xa54ff53a;
  let H4 = 0x510e527f;
  let H5 = 0x9b05688c;
  let H6 = 0x1f83d9ab;
  let H7 = 0x5be0cd19;

  const bitLength = utf8.length * 8;
  utf8.push(0x80);
  while ((utf8.length % 64) !== 56) {
    utf8.push(0);
  }

  // 64-bit length big endian
  for (let i = 7; i >= 0; i--) {
    utf8.push((bitLength / Math.pow(2, i * 8)) & 0xff);
  }

  const words: number[] = [];
  for (let i = 0; i < utf8.length; i += 4) {
    words.push((utf8[i]! << 24) | (utf8[i + 1]! << 16) | (utf8[i + 2]! << 8) | utf8[i + 3]!);
  }

  const W = new Array<number>(64);
  for (let i = 0; i < words.length; i += 16) {
    for (let t = 0; t < 16; t++) {
      W[t] = words[i + t]!;
    }
    for (let t = 16; t < 64; t++) {
      const s0 = (rotr(W[t - 15]!, 7) ^ rotr(W[t - 15]!, 18) ^ (W[t - 15]! >>> 3)) >>> 0;
      const s1 = (rotr(W[t - 2]!, 17) ^ rotr(W[t - 2]!, 19) ^ (W[t - 2]! >>> 10)) >>> 0;
      W[t] = (W[t - 16]! + s0 + W[t - 7]! + s1) >>> 0;
    }

    let a = H0;
    let b = H1;
    let c = H2;
    let d = H3;
    let e = H4;
    let f = H5;
    let g = H6;
    let h = H7;

    for (let t = 0; t < 64; t++) {
      const S1 = (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) >>> 0;
      const ch = ((e & f) ^ (~e & g)) >>> 0;
      const temp1 = (h + S1 + ch + K[t]! + W[t]!) >>> 0;
      const S0 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) >>> 0;
      const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
      const temp2 = (S0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    H0 = (H0 + a) >>> 0;
    H1 = (H1 + b) >>> 0;
    H2 = (H2 + c) >>> 0;
    H3 = (H3 + d) >>> 0;
    H4 = (H4 + e) >>> 0;
    H5 = (H5 + f) >>> 0;
    H6 = (H6 + g) >>> 0;
    H7 = (H7 + h) >>> 0;
  }

  const toHex = (n: number) => ("00000000" + n.toString(16)).slice(-8);
  return `${toHex(H0)}${toHex(H1)}${toHex(H2)}${toHex(H3)}${toHex(H4)}${toHex(H5)}${toHex(H6)}${toHex(H7)}`;
}

function rotr(x: number, n: number): number {
  return ((x >>> n) | (x << (32 - n))) >>> 0;
}

/**
 * Validates a backup manifest structure and compatibility.
 */
export function validateManifestStructure(manifest: unknown): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  if (!manifest || typeof manifest !== "object") {
    return { valid: false, errors: ["Manifesto ausente ou inválido."] };
  }

  const m = manifest as Partial<BackupManifest>;
  if (!m.id || typeof m.id !== "string") errors.push("ID do backup ausente no manifesto.");
  if (!m.createdAt || typeof m.createdAt !== "string") errors.push("Data de criação ausente no manifesto.");
  if (!m.type || !["MANUAL", "AUTOMATIC", "SAFETY_SNAPSHOT"].includes(m.type)) errors.push("Tipo de backup inválido.");
  if (!m.scope || !["FULL", "DATABASE", "FILES", "SETTINGS"].includes(m.scope)) errors.push("Escopo de backup inválido.");
  if (!m.sha256Checksum || typeof m.sha256Checksum !== "string" || m.sha256Checksum.length !== 64) {
    errors.push("Checksum SHA-256 ausente ou inválido no manifesto.");
  }
  if (typeof m.schemaVersion !== "number") {
    errors.push("Versão de schema ausente no manifesto.");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Generates the expected confirmation token for safe restoration.
 */
export function generateRestoreConfirmationToken(backupId: string): string {
  return `RESTAURAR ${backupId.trim().toUpperCase()}`;
}
